import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import {
  createReportingGroup,
  deleteReportingGroup,
  getReportingGroups,
  replaceReportingGroupManagerGrants,
  replaceReportingGroupMembers,
  updateReportingGroup,
} from "../../services/adminService";
import { createToast } from "../../utils/toastUtils";

const ReportingGroupManagement = ({ organizationId, organizations, onOrganizationChange, users }) => {
  const [groups, setGroups] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [memberIds, setMemberIds] = useState([]);
  const [grants, setGrants] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [createName, setCreateName] = useState("");
  const [createDescription, setCreateDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const createModal = useDisclosure();
  const toastHelper = createToast(useToast());

  const selectedOrganization = useMemo(
    () => organizations.find((organization) => Number(organization.id) === Number(organizationId)),
    [organizationId, organizations]
  );

  const coachedMembers = useMemo(
    () => users.filter((user) => (
      Number(user.organization_id) === Number(organizationId)
      && ["employee", "manager"].includes(user.role)
      && !user.is_admin
    )),
    [users, organizationId]
  );
  const managers = useMemo(
    () => users.filter((user) => Number(user.organization_id) === Number(organizationId) && user.role === "manager" && !user.is_admin),
    [users, organizationId]
  );

  const selectGroup = useCallback((group) => {
    setSelectedId(String(group.id));
    setName(group.name);
    setDescription(group.description || "");
    setMemberIds(group.members.map((member) => member.id));
    setGrants(Object.fromEntries(group.manager_grants.map((grant) => [grant.manager.id, grant.access_level])));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const nextGroups = await getReportingGroups(organizationId);
      setGroups(nextGroups);
      const selected = nextGroups.find((group) => String(group.id) === String(selectedId)) || nextGroups[0];
      if (selected) selectGroup(selected);
      else {
        setSelectedId("");
        setName("");
        setDescription("");
        setMemberIds([]);
        setGrants({});
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Unable to load reporting groups. Switch to this organization first.");
    } finally {
      setLoading(false);
    }
  }, [organizationId, selectGroup, selectedId]);

  useEffect(() => { load(); }, [organizationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeOrganization = (nextOrganizationId) => {
    setGroups([]);
    setSelectedId("");
    setName("");
    setDescription("");
    setMemberIds([]);
    setGrants({});
    onOrganizationChange(Number(nextOrganizationId));
  };

  const openCreateGroup = () => {
    setCreateName("");
    setCreateDescription("");
    createModal.onOpen();
  };

  const createGroup = async () => {
    if (!organizationId || !createName.trim()) return;
    setCreating(true);
    try {
      const group = await createReportingGroup(organizationId, {
        name: createName.trim(),
        description: createDescription.trim() || null,
      });
      createModal.onClose();
      await load();
      selectGroup(group);
      toastHelper.success("Reporting group created", group.name);
    } catch (err) {
      toastHelper.error("Unable to create group", err.response?.data?.detail || "Request failed");
    } finally {
      setCreating(false);
    }
  };

  const save = async () => {
    if (!selectedId || !name.trim()) return;
    setLoading(true);
    try {
      await updateReportingGroup(organizationId, selectedId, { name: name.trim(), description: description || null });
      await replaceReportingGroupMembers(organizationId, selectedId, memberIds);
      await replaceReportingGroupManagerGrants(
        organizationId,
        selectedId,
        Object.entries(grants).map(([manager_user_id, access_level]) => ({
          manager_user_id: Number(manager_user_id),
          access_level,
        }))
      );
      await load();
      toastHelper.success("Reporting group saved", name.trim());
    } catch (err) {
      toastHelper.error("Unable to save group", err.response?.data?.detail || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const remove = async () => {
    if (!selectedId || !window.confirm(`Delete reporting group “${name}”? Access will be revoked immediately.`)) return;
    try {
      await deleteReportingGroup(organizationId, selectedId);
      setSelectedId("");
      await load();
      toastHelper.success("Reporting group deleted", name);
    } catch (err) {
      toastHelper.error("Unable to delete group", err.response?.data?.detail || "Request failed");
    }
  };

  return (
    <Box borderWidth="1px" borderRadius="lg" bg="white" p={5}>
      <HStack justify="space-between" mb={4}>
        <Box>
          <Text fontWeight="semibold">Reporting Groups</Text>
          <Text fontSize="sm" color="gray.500">Assign employee visibility and viewer or coach access.</Text>
        </Box>
        <Button size="sm" onClick={openCreateGroup}>+ Create Group</Button>
      </HStack>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
        <FormControl isRequired>
          <FormLabel>Organization</FormLabel>
          <Select
            value={String(organizationId)}
            onChange={(event) => changeOrganization(event.target.value)}
          >
            {organizations.map((organization) => (
              <option key={organization.id} value={organization.id}>{organization.name}</option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Group</FormLabel>
          <Select
            value={selectedId}
            placeholder={loading ? "Loading groups…" : "Select a group"}
            isDisabled={loading || groups.length === 0}
            onChange={(event) => selectGroup(groups.find((group) => String(group.id) === event.target.value))}
          >
            {groups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
          </Select>
        </FormControl>
      </SimpleGrid>
      {error && <Alert status="error" mb={4}><AlertIcon />{error}</Alert>}
      {!error && groups.length === 0 && !loading && <Text color="gray.500">No reporting groups yet.</Text>}
      {groups.length > 0 && selectedId && (
        <VStack align="stretch" spacing={4}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl><FormLabel>Name</FormLabel><Input value={name} onChange={(event) => setName(event.target.value)} /></FormControl>
            <FormControl><FormLabel>Description</FormLabel><Input value={description} onChange={(event) => setDescription(event.target.value)} /></FormControl>
          </SimpleGrid>
          {groups.find((group) => String(group.id) === selectedId)?.source_location_id && (
            <Text fontSize="xs" color="gray.500">Migrated from location {groups.find((group) => String(group.id) === selectedId).source_location_id}; assignments are now independent.</Text>
          )}
          <Divider />
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
            <VStack align="stretch">
              <Text fontWeight="semibold">Coached team members</Text>
              <Text fontSize="sm" color="gray.500">Managers who also work with customers may be selected here and under manager access.</Text>
              {coachedMembers.map((member) => (
                <Checkbox key={member.id} isChecked={memberIds.includes(member.id)} onChange={(event) => setMemberIds(event.target.checked ? [...memberIds, member.id] : memberIds.filter((id) => id !== member.id))}>
                  {member.first_name || member.last_name ? `${member.first_name || ""} ${member.last_name || ""}`.trim() : member.email}{member.role === "manager" ? " (Manager)" : ""}
                </Checkbox>
              ))}
              {!coachedMembers.length && <Text fontSize="sm" color="gray.500">No eligible team members in this organization.</Text>}
            </VStack>
            <VStack align="stretch">
              <Text fontWeight="semibold">Manager access</Text>
              {managers.map((manager) => (
                <HStack key={manager.id}>
                  <Checkbox flex="1" isChecked={Boolean(grants[manager.id])} onChange={(event) => setGrants((current) => {
                    const next = { ...current };
                    if (event.target.checked) next[manager.id] = "viewer";
                    else delete next[manager.id];
                    return next;
                  })}>{manager.first_name || manager.last_name ? `${manager.first_name || ""} ${manager.last_name || ""}`.trim() : manager.email}</Checkbox>
                  <Select size="sm" w="120px" value={grants[manager.id] || "viewer"} isDisabled={!grants[manager.id]} onChange={(event) => setGrants((current) => ({ ...current, [manager.id]: event.target.value }))}>
                    <option value="viewer">Viewer</option>
                    <option value="coach">Coach</option>
                  </Select>
                </HStack>
              ))}
              {!managers.length && <Text fontSize="sm" color="gray.500">No managers in this organization.</Text>}
            </VStack>
          </SimpleGrid>
          <HStack justify="flex-end"><Button colorScheme="red" variant="ghost" onClick={remove}>Delete</Button><Button colorScheme="blue" isLoading={loading} onClick={save}>Save</Button></HStack>
        </VStack>
      )}
      <Modal isOpen={createModal.isOpen} onClose={createModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create Reporting Group</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <Text fontSize="sm" color="gray.500">Organization</Text>
                <Text fontWeight="semibold">{selectedOrganization?.name || `#${organizationId}`}</Text>
              </Box>
              <FormControl isRequired>
                <FormLabel>Name</FormLabel>
                <Input value={createName} onChange={(event) => setCreateName(event.target.value)} autoFocus />
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Input value={createDescription} onChange={(event) => setCreateDescription(event.target.value)} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={createModal.onClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={createGroup} isLoading={creating} isDisabled={!createName.trim()}>
              Create Group
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ReportingGroupManagement;
